/**
 * Opt-in on-device viewport diagnostics: open the app once with `?debug=1` (persists in localStorage; `?debug=0` clears)
 * and a small overlay prints innerHeight / visualViewport / screen / safe-area / scroll offsets / display-mode every second.
 * Used to chase iOS Home-Screen sizing bugs (black bar at the bottom) where remote debugging isn't available.
 */
let taps: number[] = [];
/** Call on each tap of a hidden trigger; 5 taps within 3s toggles the overlay. Returns the new state, or null when not toggled. */
export function toggleViewportDebug(): boolean | null {
  const now = Date.now();
  taps = [...taps.filter((t) => now - t < 3000), now];
  if (taps.length < 5) return null;
  taps = [];
  const on = localStorage.getItem('mos.debug') === '1';
  if (on) localStorage.removeItem('mos.debug');
  else localStorage.setItem('mos.debug', '1');
  return !on;
}

export function installViewportDebug(): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const q = new URLSearchParams(location.search).get('debug');
  if (q === '1') localStorage.setItem('mos.debug', '1');
  if (q === '0') localStorage.removeItem('mos.debug');
  if (localStorage.getItem('mos.debug') !== '1') return () => undefined;
  const el = document.createElement('pre');
  el.id = 'mos-vp-debug';
  el.style.cssText = 'position:fixed;left:8px;right:8px;top:calc(env(safe-area-inset-top,0px) + 60px);z-index:2147483647;margin:0;padding:8px 10px;border-radius:10px;background:rgba(0,0,0,.78);color:#0f0;font:11px/1.35 ui-monospace,Menlo,monospace;pointer-events:none;white-space:pre-wrap;direction:ltr;text-align:left';
  document.body.appendChild(el);
  const css = (v: string) => getComputedStyle(document.documentElement).getPropertyValue(v).trim();
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;left:0;top:0;width:0;height:0;padding-top:env(safe-area-inset-top,0px);padding-bottom:env(safe-area-inset-bottom,0px);visibility:hidden;pointer-events:none';
  document.body.appendChild(probe);
  const tick = () => {
    const vv = window.visualViewport;
    const shell = document.querySelector<HTMLElement>('.m-app-shell');
    const r = shell?.getBoundingClientRect();
    const ps = getComputedStyle(probe);
    const standalone = (navigator as Navigator & { standalone?: boolean }).standalone === true || matchMedia('(display-mode: standalone)').matches;
    el.textContent = [
      `standalone=${standalone} dm=${['fullscreen', 'standalone', 'minimal-ui', 'browser'].find((m) => matchMedia(`(display-mode: ${m})`).matches) ?? '?'}`,
      `inner=${window.innerWidth}x${window.innerHeight} outer=${window.outerWidth}x${window.outerHeight} dpr=${devicePixelRatio}`,
      `screen=${screen.width}x${screen.height} avail=${screen.availWidth}x${screen.availHeight}`,
      `vv=${vv ? `${Math.round(vv.width)}x${Math.round(vv.height)} off=${Math.round(vv.offsetTop)},${Math.round(vv.offsetLeft)} scale=${vv.scale.toFixed(2)}` : 'n/a'}`,
      `docEl=${document.documentElement.clientWidth}x${document.documentElement.clientHeight} scrollH=${document.documentElement.scrollHeight}`,
      `safe top=${ps.paddingTop} bottom=${ps.paddingBottom}`,
      `shell=${r ? `${Math.round(r.top)}..${Math.round(r.bottom)} h=${Math.round(r.height)}` : 'none'} shellScrollTop=${shell?.scrollTop ?? '-'} winY=${window.scrollY} rootY=${document.getElementById('root')?.scrollTop ?? '-'} bodyY=${document.body.scrollTop} htmlY=${document.documentElement.scrollTop}`,
      `bg=${css('--m-color-bg-canvas')} ua=${navigator.userAgent.replace(/Mozilla\/5\.0 \(/, '(').slice(0, 90)}`,
    ].join('\n');
  };
  tick();
  const id = window.setInterval(tick, 1000);
  return () => {
    window.clearInterval(id);
    el.remove();
    probe.remove();
  };
}
